import React, { useEffect } from 'react';
import { UserRoleType, type SignUpFormDto } from 'types/types';
import { useAppSelector, useForm } from 'hooks';
import { SubmitHandler } from 'react-hook-form';
import styles from './styles.module.sass';
import { AuthError, DataStatus, UserRole } from 'types/enums';
import { Input, RadioGroup } from 'components';
import { EMAIL_PATTERN, getDefaultValues, ROLE_RADIO_VALUES } from './constants';
import { Box, Button } from '@mui/material';
import { PatientForm } from './components/patient-form';
import { FacilitySelector } from './components/facility-selector';

type Props = {
  onSubmit: (data: SignUpFormDto) => void;
  isAdmin?: boolean;
  title?: string;
};

export const SignUpForm: React.FC<Props> = ({ onSubmit, isAdmin = false, title = 'Account Data' }) => {
  const { status, createAdminStatus } = useAppSelector((state) => state.user);

  const isLoading = status === DataStatus.LOADING;
  const shouldBeReset = createAdminStatus === DataStatus.SUCCEEDED;

  const { control, handleSubmit, reset, watch } = useForm<SignUpFormDto>({
    defaultValues: getDefaultValues(isAdmin ? UserRole.ADMIN : UserRole.PATIENT),
  });

  const chosenRole = +watch('role') as UserRoleType;

  const onFormSubmit: SubmitHandler<SignUpFormDto> = (payload) => {
    onSubmit(payload);
  };

  useEffect(() => {
    if (shouldBeReset) {
      reset();
    }
  }, [shouldBeReset, reset]);

  return (
    <Box className={styles.container} data-testid='ps-sign-up-form-container-test-id'>
      <form className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
        <h2>{title}</h2>
        <Input
          rules={{ required: { value: true, message: AuthError.USER_NAME_REQUIRED } }}
          label='User name'
          placeholder='Enter you user name'
          control={control}
          name='username'
          autoFocus
          fullWidth
        />
        <Input
          rules={{ required: { value: true, message: AuthError.FIRST_NAME_REQUIRED } }}
          label='First Name'
          placeholder='Enter you first name'
          control={control}
          name='firstName'
          fullWidth
        />
        <Input
          rules={{ required: { value: true, message: AuthError.LAST_NAME_REQUIRED } }}
          label='Last Name'
          placeholder='Enter you last name'
          control={control}
          name='lastName'
          fullWidth
        />
        <Input
          rules={{ required: { value: true, message: AuthError.STUDY_REQUIRED } }}
          label='Study Number'
          placeholder='Enter study number'
          control={control}
          name='study'
          fullWidth
        />
        {isAdmin && (
          <Input
            label='Email'
            rules={{
              required: { value: true, message: AuthError.EMAIL_REQUIRED },
              pattern: {
                value: EMAIL_PATTERN,
                message: AuthError.EMAIL_INVALID,
              },
            }}
            placeholder='Enter admin email'
            control={control}
            name='email'
            fullWidth
          />
        )}
        {!isAdmin && <RadioGroup label='Choose your role' name='role' control={control} values={ROLE_RADIO_VALUES} />}
        {chosenRole === UserRole.PATIENT && (
          <PatientForm
            fieldNames={{
              dateOfBirth: 'dateOfBirth',
              medicalRecordNumber: 'medicalRecordNumber',
              race: 'race',
              gender: 'gender',
              conditions: 'conditions',
            }}
            control={control}
          />
        )}
        <FacilitySelector role={chosenRole} control={control} name={'facilities'} />
        <Button disabled={isLoading} variant='contained' type='submit'>
          Submit
        </Button>
      </form>
    </Box>
  );
};
